/**
 * StylePlayground.tsx - Interactive style testing environment
 * 
 * CHANGE HISTORY:
 * - 251102_1750 danielbeer: Initial creation for Phase 3.6b
 * 
 * Meta management tool for testing and experimenting with styles
 */

import React, { useState, useEffect } from 'react';
import { getStylr } from '../StylrManager';
import { getAnimationManager } from '../features/AnimationManager';
import { getStyleValidator } from '../features/StyleValidator';
import type { StyleVariables } from '../types';

export interface StylePlaygroundProps {
  styleId?: string;
  mode?: 'interactive' | 'comparison';
  onClose?: () => void;
}

type ComponentType = 'button' | 'card' | 'input' | 'badge' | 'modal';
type ViewportSize = 'mobile' | 'tablet' | 'desktop';

export const StylePlayground: React.FC<StylePlaygroundProps> = ({
  styleId,
  mode = 'interactive',
  onClose
}) => {
  const [code, setCode] = useState('');
  const [variables, setVariables] = useState<Partial<StyleVariables>>({});
  const [selectedComponent, setSelectedComponent] = useState<ComponentType>('button');
  const [viewport, setViewport] = useState<ViewportSize>('desktop');
  const [showStates, setShowStates] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [inheritanceChain, setInheritanceChain] = useState<string[]>([]);

  useEffect(() => {
    if (styleId) {
      const stylr = getStylr();
      const style = stylr.getStyle(styleId);
      if (style) {
        setVariables(style.variables);
        setCode(generateCodeFromVariables(style.variables));
        
        // Get inheritance chain
        const chain = stylr.getInheritanceChain(styleId);
        if (chain) {
          setInheritanceChain(chain.styles.map((s: any) => s.name));
        }
      }
    }
  }, [styleId]);

  const generateCodeFromVariables = (vars: Partial<StyleVariables>): string => {
    return Object.entries(vars)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n');
  };

  const parseCodeToVariables = (codeStr: string): Partial<StyleVariables> => {
    const vars: Partial<StyleVariables> = {};
    const lines = codeStr.split('\n');
    
    lines.forEach(line => {
      const match = line.match(/^(--[\w-]+):\s*(.+);?$/);
      if (match) {
        const [, key, value] = match;
        vars[key as keyof StyleVariables] = value.trim() as any;
      }
    });
    
    return vars;
  };

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
    const newVars = parseCodeToVariables(newCode);
    setVariables(newVars);
    
    // Validate
    const validator = getStyleValidator();
    const result = validator.validate(newVars as any);
    if (!result.valid) {
      setValidationErrors(result.errors.map(e => `${e.variable}: ${e.message}`));
    } else {
      setValidationErrors([]);
    }
  };

  const applyToDocument = () => {
    const root = document.documentElement;
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, String(value));
    });
  };

  const exportAsStyle = () => {
    const stylr = getStylr();
    const newStyle = stylr.createStyle({
      name: `Playground Style ${Date.now()}`,
      type: 'custom',
      variables: variables as any
    });
    
    alert(`Style created with ID: ${newStyle.id}`);
  };

  const getViewportWidth = (): string => {
    switch (viewport) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      case 'desktop': return '100%';
    }
  };

  const renderComponent = (state?: 'hover' | 'active' | 'disabled') => {
    const baseStyle: React.CSSProperties = {};
    
    // Apply variables as inline styles (commented out for now - direct CSS var usage below)
    // Object.entries(variables).forEach(([key, value]) => {
    //   const cssKey = key.replace(/^--/, '').replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    //   baseStyle[cssKey as any] = value;
    // });

    switch (selectedComponent) {
      case 'button':
        return (
          <button
            disabled={state === 'disabled'}
            style={{
              padding: variables['--padding'] || '10px 20px',
              background: String(variables['--bg'] || '#3b82f6'),
              color: String(variables['--text'] || '#ffffff'),
              border: String(variables['--border'] || 'none'),
              borderRadius: String(variables['--radius'] || '6px'),
              fontSize: String(variables['--font-size'] || '14px'),
              fontWeight: String(variables['--font-weight'] || '600'),
              cursor: state === 'disabled' ? 'not-allowed' : 'pointer',
              opacity: state === 'disabled' ? 0.5 : 1,
              transform: state === 'active' ? 'scale(0.98)' : 'scale(1)',
              boxShadow: state === 'hover' ? '0 4px 12px rgba(0,0,0,0.15)' : String(variables['--box-shadow'] || '')
            }}
          >
            {state ? `Button (${state})` : 'Button'}
          </button>
        );

      case 'card':
        return (
          <div style={{
            padding: variables['--padding'] || '24px',
            background: String(variables['--bg'] || '#ffffff'),
            border: String(variables['--border'] || '1px solid #e5e7eb'),
            borderRadius: String(variables['--radius'] || '12px'),
            boxShadow: String(variables['--box-shadow'] || '0 4px 6px rgba(0,0,0,0.1)'),
            maxWidth: '400px'
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0',
              color: String(variables['--text'] || '#111827'),
              fontSize: String(variables['--font-size'] || '18px')
            }}>
              Card Title
            </h3>
            <p style={{ 
              margin: 0,
              color: String(variables['--text-secondary'] || '#6b7280'),
              fontSize: '14px'
            }}>
              This is a preview card with your custom styles applied.
            </p>
          </div>
        );

      case 'input':
        return (
          <input
            type="text"
            placeholder="Enter text..."
            disabled={state === 'disabled'}
            style={{
              width: '300px',
              padding: variables['--padding'] || '10px 14px',
              background: String(variables['--bg'] || '#ffffff'),
              color: String(variables['--text'] || '#111827'),
              border: String(variables['--border'] || '1px solid #e5e7eb'),
              borderRadius: String(variables['--radius'] || '6px'),
              fontSize: String(variables['--font-size'] || '14px'),
              outline: state === 'active' ? `2px solid ${variables['--border-focus'] || '#3b82f6'}` : 'none'
            }}
          />
        );

      case 'badge':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: variables['--padding'] || '4px 12px',
            background: String(variables['--bg'] || '#3b82f6'),
            color: String(variables['--text'] || '#ffffff'),
            borderRadius: String(variables['--radius'] || '12px'),
            fontSize: String(variables['--font-size'] || '12px'),
            fontWeight: String(variables['--font-weight'] || '600')
          }}>
            Badge
          </span>
        );

      case 'modal':
        return (
          <div style={{
            padding: variables['--padding'] || '24px',
            background: String(variables['--bg'] || '#ffffff'),
            borderRadius: String(variables['--radius'] || '12px'),
            boxShadow: String(variables['--box-shadow'] || '0 20px 25px rgba(0,0,0,0.15)'),
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ 
              margin: '0 0 16px 0',
              color: variables['--text'] || '#111827'
            }}>
              Modal Dialog
            </h2>
            <p style={{ 
              margin: '0 0 24px 0',
              color: variables['--text-secondary'] || '#6b7280'
            }}>
              This is a modal dialog preview with your custom styles.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>
                Cancel
              </button>
              <button style={{
                padding: '8px 16px',
                background: String(variables['--bg-primary'] || '#3b82f6'),
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>
                Confirm
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      height: '100vh',
      fontFamily: 'system-ui, sans-serif',
      background: '#f9fafb'
    }}>
      {/* Code Editor */}
      <div style={{ 
        width: '400px', 
        display: 'flex',
        flexDirection: 'column',
        background: '#1e1e1e',
        color: '#d4d4d4'
      }}>
        <div style={{ 
          padding: '16px', 
          borderBottom: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '14px' }}>CSS Variables</h3>
          <button
            onClick={applyToDocument}
            style={{
              padding: '6px 12px',
              background: '#0e639c',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Apply Live
          </button>
        </div>

        <textarea
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder="--bg: #ffffff;&#10;--text: #000000;&#10;--padding: 12px 24px;"
          style={{
            flex: 1,
            padding: '16px',
            background: '#1e1e1e',
            color: '#d4d4d4',
            border: 'none',
            fontFamily: 'Monaco, Consolas, monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            resize: 'none',
            outline: 'none'
          }}
        />

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#5a1d1d',
            borderTop: '1px solid #333',
            fontSize: '12px',
            maxHeight: '150px',
            overflowY: 'auto'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', color: '#f87171' }}>
              Validation Errors:
            </div>
            {validationErrors.map((error, i) => (
              <div key={i} style={{ marginBottom: '4px', color: '#fca5a5' }}>
                • {error}
              </div>
            ))}
          </div>
        )}

        {/* Inheritance Chain */}
        {inheritanceChain.length > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#252526',
            borderTop: '1px solid #333',
            fontSize: '12px'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>
              Inheritance Chain:
            </div>
            <div style={{ color: '#9cdcfe' }}>
              {inheritanceChain.join(' → ')}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid #333',
          display: 'flex',
          gap: '8px'
        }}>
          <button
            onClick={exportAsStyle}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#0e639c',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Export as Style
          </button>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                background: '#3e3e42',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Preview Panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{
          padding: '16px 24px',
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '24px',
          alignItems: 'center'
        }}>
          {/* Component Selector */}
          <div>
            <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '8px' }}>
              Component:
            </label>
            <select
              value={selectedComponent}
              onChange={(e) => setSelectedComponent(e.target.value as ComponentType)}
              style={{
                padding: '6px 10px',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                fontSize: '13px'
              }}
            >
              <option value="button">Button</option>
              <option value="card">Card</option>
              <option value="input">Input</option>
              <option value="badge">Badge</option>
              <option value="modal">Modal</option>
            </select>
          </div>

          {/* Viewport Selector */}
          <div>
            <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '8px' }}>
              Viewport:
            </label>
            <div style={{ display: 'inline-flex', gap: '4px' }}>
              {(['mobile', 'tablet', 'desktop'] as ViewportSize[]).map(size => (
                <button
                  key={size}
                  onClick={() => setViewport(size)}
                  style={{
                    padding: '6px 12px',
                    background: viewport === size ? '#3b82f6' : '#f3f4f6',
                    color: viewport === size ? '#fff' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Show States Toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={showStates}
              onChange={(e) => setShowStates(e.target.checked)}
            />
            Show States
          </label>
        </div>

        {/* Preview Area */}
        <div style={{
          flex: 1,
          padding: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f9fafb',
          overflowY: 'auto'
        }}>
          <div style={{
            width: getViewportWidth(),
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            alignItems: 'center'
          }}>
            {/* Normal State */}
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
                Normal
              </div>
              {renderComponent()}
            </div>

            {/* Other States */}
            {showStates && (
              <>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
                    Hover
                  </div>
                  {renderComponent('hover')}
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
                    Active
                  </div>
                  {renderComponent('active')}
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
                    Disabled
                  </div>
                  {renderComponent('disabled')}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StylePlayground;
